<template>
    <div class="line-chart" :class="{ 'grow-chart': grow_chart }">
        <div
            v-if="title"
            class="font-weight-bold text-h6 mb-4"
            :class="{
                'text-left': title_alignment === 'left',
                'text-center': title_alignment === 'center',
                'text-right': title_alignment === 'right'
            }"
        >
            {{ title }}
        </div>

        <line-chart-generator
            class="line-chart-generator"
            :chartOptions="chart_options"
            :chartData="chart_data"
            style="height: 350px"
        />
    </div>
</template>

<script>
import _ from 'lodash';
import { Line as LineChartGenerator } from 'vue-chartjs';
import { Chart as ChartJS, Title, Tooltip, Legend, LineElement, CategoryScale, LinearScale, PointElement } from 'chart.js';

ChartJS.register(Title, Tooltip, Legend, LineElement, CategoryScale, LinearScale, PointElement);

export default {
    components: {
        LineChartGenerator
    },

    props: {
        title: {
            type: String,
            default: function () { return "Set title to properly title the chart!" }
        },

        title_alignment: {
            type: String,
            default: function () { return "center"; }
        },

        currency: {
            type: Boolean,
            default: function () { return false; }
        },

        decimal_y_axis: {
            type: Boolean,
            default: function () { return true; }
        },

        labels: {
            type: Array,
            default: function () { return []; }
        },
        point_label: {
            type: String,
            default: function () { return "Opt-in"; }
        },

        data: {
            type: Array,
            default: function () { return []; }
        },

        /**
         * Whether the chart should grow vertically to fit its container
         */
        grow_chart: {
            type: Boolean,
            default: function () { return false; }
        },
    },

    computed: {
        chart_options() {
            let ret = {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    x: {
                        grid: {
                            display: true,
                            color: 'rgba(0,0,0,0.1)'
                        }
                    },
                    y: {
                        grid: {
                            display: true,
                            color: 'rgba(0,0,0,0.1)'
                        },
                        beginAtZero: true,
                        // make sure there's a bit of space above the highest point
                        ...((!this.decimal_y_axis) && {
                            suggestedMax: Math.max(...this.data) + 1
                        })
                    }
                },
                plugins: {
                    tooltip: {
                        callbacks: {
                            title: (context) => {
                                return context[0].label;
                            },
                            label: (context) => {
                                const value = context.formattedValue;
                                return `${value} ${this.point_label}${value !== '1' ? 's' : ''}`;
                            }
                        },
                    },
                    legend: {
                        display: false
                    }
                },
                elements: {
                    line: {
                        tension: 0.4
                    },
                    point: {
                        radius: 6,
                        hoverRadius: 8
                    }
                }
            };

            if (this.currency) {
                ret.scales.y.ticks = {
                    callback: function (value, index, values) {
                        return '$' + value.toLocaleString();
                    }
                };
            } else if (!this.decimal_y_axis) {
                ret.scales.y.ticks = {
                    callback: function (value, index, values) {
                        if (Math.floor(value) === value) {
                            return value;
                        }
                    }
                };
            }

            return ret;
        },

        chart_data() {
            return {
                labels: this.labels,
                datasets: [{
                    label: this.point_label,
                    backgroundColor: 'rgba(242, 70, 83, 0.1)',
                    borderColor: '#f24653',
                    pointBackgroundColor: '#f24653',
                    pointBorderColor: '#fff',
                    pointBorderWidth: 2,
                    pointHoverBackgroundColor: '#f24653',
                    pointHoverBorderColor: '#fff',
                    pointHoverBorderWidth: 3,
                    pointHoverRadius: 8,
                    data: this.data,
                    fill: true
                }],
            };
        },
    },
}
</script>

<style lang="scss" scoped>
.line-chart.grow-chart {
  height: 100% !important;

  .line-chart-generator {
    /* minimum 350px height */
    height: max(100%, 350px) !important;
  }
}
</style>
